import React, { useEffect, useState } from "react";
import ResponsiveImg, { SIZES } from "../components/ResponsiveImg";
import Link from "../components/LocalizedLink";
import { useI18n } from "../i18n/I18nProvider";
import SchemaMarkup, { buildBreadcrumb } from "../components/SchemaMarkup";
import useDocumentMeta from "../hooks/useDocumentMeta";
import { lifestylePillars } from "../data/content";
import { api } from "../lib/api";

const FALLBACK = {
  title: "About Finca Cortesín",
  eyebrow: "About",
  heading:
    "A private estate of <em>215 hectares</em>, quietly held since 2006.",
  intro: "",
  stats: [],
  cta_label: "Book Private Consultation",
  cta_link: "/contact?request=consultation",
  seo: {
    title: "About Finca Cortesín | Five-Star Golf Resort in Casares, Spain",
    description:
      "Finca Cortesín is a 215-hectare private resort on the Costa del Sol, home to a championship golf course, beach club, spa and Leading Hotels of the World hotel.",
  },
};

export default function About() {
  const [page, setPage] = useState(FALLBACK);
  const { t } = useI18n();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pages/about");
        setPage({ ...FALLBACK, ...data });
      } catch (_err) { /* ignore */ }
    })();
  }, []);

  useDocumentMeta({
    title: page.seo?.title,
    description: page.seo?.description,
  });

  return (
    <div className="bg-[#F9F6F0] text-[#1A1A1A]">
      <SchemaMarkup schema={buildBreadcrumb([{ name: t("home"), url: "/" }, { name: t("about_breadcrumb"), url: "/about" }])} />

      <section className="pt-40 pb-20 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-5">— {page.eyebrow || "About"}</p>
            <h1
              data-testid="about-title"
              className="text-5xl sm:text-6xl lg:text-7xl font-light leading-[0.95]"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              dangerouslySetInnerHTML={{ __html: page.heading || "" }}
            />
          </div>
          <div className="lg:col-span-6 lg:col-start-7 prose prose-stone max-w-none text-lg text-[#3D3B38] leading-relaxed" dangerouslySetInnerHTML={{ __html: page.intro || "" }} />
        </div>
      </section>

      <section className="py-20 lg:py-28 bg-[#FFFFFF] border-y border-[#E5DFD5]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">— {t("about_pillars_eyebrow")}</p>
          <h2 className="text-4xl lg:text-5xl font-light mb-16 max-w-3xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {t("about_pillars_heading")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {lifestylePillars.map((p, i) => (
              <div key={p.key} className="flex flex-col">
                <div className="aspect-[5/4] overflow-hidden mb-6">
                  <ResponsiveImg src={p.image} sizes={SIZES.third} alt={t(`pillar_${p.key}_title`)} className="w-full h-full object-cover" />
                </div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-3">0{i + 1}</p>
                <h3 className="text-2xl font-light mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{t(`pillar_${p.key}_title`)}</h3>
                <p className="text-base text-[#73706B] leading-relaxed">{t(`pillar_${p.key}_body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {(page.stats || []).length > 0 && (
        <section className="py-20 lg:py-28 bg-[#1A1A1A] text-[#F9F6F0]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">— {t("about_numbers_eyebrow")}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 mt-12">
              {page.stats.map((x) => (
                <div key={x.l} className="border-l border-[#333] pl-5">
                  <p className="text-4xl lg:text-5xl font-light" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{x.k}</p>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A39A] mt-2">{x.l}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.cta_label && (
        <section className="py-20">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8 border border-[#E5DFD5] p-10 lg:p-16">
            <h2 className="text-3xl lg:text-4xl font-light max-w-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {t("begin_exploration")}
            </h2>
            <Link to={page.cta_link || "/contact"} data-testid="about-cta" className="bg-[#1A1A1A] text-[#F9F6F0] px-8 py-4 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors">
              {page.cta_label}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
